<template>
  <div :class="wrapperClass">
    <div class="bg-white border border-gray-200 rounded-2xl shadow-sm p-0 overflow-hidden">
      <slot name="tabs"></slot>
      <slot name="filters"></slot>

      <div
        v-if="showHeader"
        class="flex items-center justify-between px-5 pt-4 pb-3 border-gray-200 my-4"
      >
        <div class="w-64">
          <div class="flex items-center rounded-lg border border-gray-300 pl-2">
            <svg
              class="h-4 w-4 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-width="2"
                d="M21 21l-4.3-4.3M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
              />
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="searchPlaceholder"
              class="flex-1 h-9 text-sm border-none focus:ring-0"
            />
          </div>
        </div>

        <div class="flex-1 flex justify-center">
          <label class="inline-flex items-center gap-2 text-sm text-gray-600">
            <select
              v-model.number="rowsPerPageLocal"
              class="h-9 rounded-md border border-gray-300 px-5 text-sm focus:outline-none"
            >
              <option v-for="opt in rowsPerPageOptions" :key="opt" :value="opt">
                {{ opt }} per page
              </option>
            </select>
          </label>
        </div>

        <div class="w-64 flex justify-end">
          <slot name="header-actions">
            <button
              v-if="showAddButton"
              type="button"
              class="inline-flex items-center gap-2 h-9 px-4 rounded-full font-bold text-white text-sm shadow-sm hover:opacity-95"
              :style="{ backgroundColor: addButtonColor }"
              @click="handleAddClick"
            >
              {{ addButtonLabel }}
            </button>
          </slot>
        </div>
      </div>

      <div class="w-full">
        <div class="overflow-x-auto">
          <table class="w-full table-fixed">
            <thead>
              <tr class="text-xs text-gray-600 tracking-tight">
                <th
                  v-for="column in columns"
                  :key="column.key"
                  class="whitespace-nowrap px-3 py-2 h-[50px] border-b-[2px] border-gray-200 font-medium align-middle"
                  :class="
                    column.key === 'action'
                      ? 'text-center'
                      : column.align === 'right'
                        ? 'text-right'
                        : column.align === 'center'
                          ? 'text-center'
                          : 'text-left'
                  "
                  :style="column.width ? { width: column.width } : null"
                >
                  <span>{{ column.label }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in paginatedRows"
                :key="rowKey(row)"
                :class="[rowClassProp(row), 'group text-xs']"
              >
                <td
                  v-for="column in columns"
                  :key="column.key"
                  class="px-3 py-3 border-b-[1px] border-gray-100 align-middle"
                  :class="
                    column.key === 'action'
                      ? 'text-center'
                      : column.align === 'right'
                        ? 'text-right'
                        : column.align === 'center'
                          ? 'text-center'
                          : 'text-left'
                  "
                  :style="column.width ? { width: column.width } : null"
                >
                  <div :style="column.maxWidth ? { maxWidth: column.maxWidth } : null">
                    <slot
                      v-if="$slots[column.key]"
                      :name="column.key"
                      :row="row"
                      :value="row[column.key]"
                    />

                    <slot
                      v-else-if="$slots[`cell:${column.key}`]"
                      :name="`cell:${column.key}`"
                      :value="row[column.key]"
                      :row="row"
                    />

                    <template v-else-if="column.key === 'action'">
                      <slot name="action" :row="row">
                        <div class="flex items-center justify-center gap-3 text-[13px]">
                          <button
                            v-if="showView"
                            class="text-gray-700"
                            @click="$emit('row-action', { action: 'view', row })"
                          >
                            <i class="fas fa-eye"></i> View
                          </button>
                          <button
                            v-if="showEdit"
                            class="text-gray-700"
                            @click="$emit('row-action', { action: 'edit', row })"
                          >
                            <i class="fas fa-edit"></i> Edit
                          </button>
                        </div>
                      </slot>
                    </template>

                    <span v-else class="-medium">{{ row[column.key] }}</span>
                  </div>
                </td>
              </tr>
              <tr v-if="!paginatedRows.length">
                <td :colspan="columns.length" class="px-4 py-10 text-center text-gray-400">
                  No data available
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-show="showPagination"
        class="flex justify-center items-center gap-2 py-5 border-t border-gray-100"
      >
        <button
          v-if="currentPage > 1"
          @click="changePage(currentPage - 1)"
          class="w-9 h-9 rounded-full hover:bg-gray-100 transition"
        >
          ‹
        </button>
        <ul class="flex gap-2">
          <li v-for="page in totalPages" :key="page">
            <button
              @click.prevent="changePage(page)"
              :class="[
                'inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-medium transition',
                page === currentPage
                  ? 'bg-[#5B9AC5] text-white shadow-sm'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-200',
              ]"
            >
              {{ page }}
            </button>
          </li>
        </ul>
        <button
          v-if="currentPage < totalPages"
          @click="changePage(currentPage + 1)"
          class="w-9 h-9 rounded-full hover:bg-gray-100 transition"
        >
          ›
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DynamicTable',
  props: {
    wrapperClass: { type: String, default: 'max-w-full mb-4' },
    rows: { type: Array, required: true },
    columns: { type: Array, required: true },
    rowsPerPage: { type: Number, default: 50 },
    rowsPerPageOptions: { type: Array, default: () => [10, 20, 50] },
    rowClass: { type: Function, default: () => '' },
    showHeader: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: 'Search' },
    externalSearch: { type: String, default: null },
    showAddButton: { type: Boolean, default: false },
    addButtonLabel: { type: String, default: 'New' },
    addButtonColor: { type: String, default: '#5B9AC5' },
    showEdit: { type: Boolean, default: true },
    showView: { type: Boolean, default: false },
    showDelete: { type: Boolean, default: true },
    actions: { type: Array, default: () => [] },
    // --- Optional server-side pagination (all default to off; existing usages are unaffected) ---
    serverSide: { type: Boolean, default: false },
    totalRows: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    // --- Optional server-side search debounce (only relevant when serverSide is true) ---
    searchDebounceMs: { type: Number, default: 400 },
  },
  emits: ['add', 'row-action', 'update:page', 'update:rowsPerPage', 'update:search'],
  data() {
    return {
      searchQuery: '',
      selectedRow: null,
      showActionsModal: false,
      internalPage: 1,
      rowsPerPageLocal: this.rowsPerPage,
    }
  },
  computed: {
    currentPage() {
      return this.serverSide ? this.page : this.internalPage
    },
    searchKeys() {
      return (this.columns || []).map((c) => c.key).filter((k) => k && k !== 'action')
    },
    effectiveSearch() {
      return (this.externalSearch ?? this.searchQuery).trim().toLowerCase()
    },
    searchedRows() {
      // Only meaningful in client-side mode. In server-side mode `rows` is
      // already the filtered/paginated page from the API, so this is unused.
      const q = this.effectiveSearch
      if (!q) return this.rows
      return this.rows.filter((row) => {
        const hay = this.searchKeys
          .map((k) => this.flattenValue(row[k]))
          .join(' ')
          .toLowerCase()
        return hay.includes(q)
      })
    },
    paginatedRows() {
      // Server-side mode: `rows` is already just the current page's data.
      if (this.serverSide) return this.rows
      const start = (this.internalPage - 1) * this.rowsPerPageLocal
      return this.searchedRows.slice(start, start + this.rowsPerPageLocal)
    },
    totalPages() {
      if (this.serverSide) return Math.max(1, Math.ceil(this.totalRows / this.rowsPerPageLocal))
      return Math.max(1, Math.ceil(this.searchedRows.length / this.rowsPerPageLocal))
    },
    showPagination() {
      return this.totalPages > 1
    },
  },
  watch: {
    rowsPerPage(newVal) {
      if (newVal !== this.rowsPerPageLocal) {
        this.rowsPerPageLocal = newVal
        this.resetPage()
      }
    },
    rowsPerPageLocal(newVal) {
      this.resetPage()
      if (this.serverSide) this.$emit('update:rowsPerPage', newVal)
    },
    // Existing client-side-only behavior is preserved untouched.
    // New: when serverSide is on, debounce and emit the term upward instead
    // of filtering locally, since `rows` is only ever the current page.
    searchQuery(newVal) {
      if (!this.serverSide) {
        this.internalPage = 1
        return
      }
      clearTimeout(this._searchDebounce)
      this._searchDebounce = setTimeout(() => {
        this.$emit('update:search', (newVal || '').trim())
      }, this.searchDebounceMs)
    },
    externalSearch() {
      if (!this.serverSide) this.internalPage = 1
    },
    rows() {
      if (!this.serverSide) this.internalPage = 1
    },
  },
  beforeUnmount() {
    clearTimeout(this._searchDebounce)
  },
  methods: {
    handleAddClick() {
      this.$emit('add')
    },
    flattenValue(val) {
      if (val == null) return ''
      if (['string', 'number', 'boolean'].includes(typeof val)) return String(val)
      if (Array.isArray(val)) return val.map(this.flattenValue).join(' ')
      if (typeof val === 'object') return Object.values(val).map(this.flattenValue).join(' ')
      return ''
    },
    resetPage() {
      if (this.serverSide) this.$emit('update:page', 1)
      else this.internalPage = 1
    },
    changePage(p) {
      if (this.serverSide) this.$emit('update:page', p)
      else this.internalPage = p
    },
    rowKey(row) {
      return row?.id ?? row?.bookingId ?? JSON.stringify(row)
    },
    rowClassProp(row) {
      return this.rowClass ? this.rowClass(row) : ''
    },
  },
}
</script>

<style scoped>
:deep(td) {
  @apply text-sm;
}
</style>
